/** Cognito group names — must match User Pool groups in AWS. */
export const COGNITO_GROUP_ADMIN = "admin";
export const COGNITO_GROUP_USER = "user";

/**
 * @param {string[] | string | undefined} raw — ID token `cognito:groups` claim
 * @returns {string[]}
 */
export function normalizeCognitoGroups(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  if (typeof raw === "string") return [raw];
  return [];
}

/**
 * @param {string[]} groups
 * @returns {boolean}
 */
export function isAdminFromGroups(groups) {
  return groups.includes(COGNITO_GROUP_ADMIN);
}

/**
 * Primary Halo role for UI gating (admin wins if both groups present).
 * @param {string[]} groups
 * @returns {'admin' | 'user' | null}
 */
export function resolvePrimaryRole(groups) {
  if (isAdminFromGroups(groups)) return "admin";
  if (groups.includes(COGNITO_GROUP_USER)) return "user";
  return null;
}

/**
 * @param {Record<string, unknown>} payload — decoded Cognito ID token
 * @returns {{ email: string, sub: string, username: string, groups: string[], role: 'admin' | 'user' | null }}
 */
export function userFromIdTokenPayload(payload) {
  const groups = normalizeCognitoGroups(payload["cognito:groups"]);
  return {
    email: String(payload.email || payload["cognito:username"] || ""),
    sub: String(payload.sub || ""),
    username: String(payload["cognito:username"] || payload.sub || ""),
    groups,
    role: resolvePrimaryRole(groups),
  };
}

/**
 * Local dev when Cognito env is unset (see cognitoPool.isLocalDevBypassEnabled).
 * @param {string} [email]
 * @returns {ReturnType<typeof userFromIdTokenPayload>}
 */
export function buildDevBypassUser(email) {
  const roleEnv = (process.env.REACT_APP_DEV_BYPASS_ROLE || "user").toLowerCase();
  const role = roleEnv === "admin" ? "admin" : "user";
  const groups = role === "admin" ? [COGNITO_GROUP_ADMIN] : [COGNITO_GROUP_USER];
  const resolvedEmail = (email || "").trim() || "dev@localhost";
  return {
    email: resolvedEmail,
    sub: "local-dev-bypass",
    username: "local-dev-bypass",
    groups,
    role,
  };
}
